import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Wallet, CircleDollarSign, Gift, Clock, RefreshCw } from 'lucide-react';
import { adminApi, formatCurrency, formatDate, extractErrorMessage } from '@kardume/services';
import type { CashbackConfigDto, CashbackStatus, Tone } from '@kardume/types';
import { useNotification } from '@kardume/hooks';
import {
  PageHeader,
  Card,
  CardHeader,
  CardBody,
  Table,
  TableContainer,
  Badge,
  Button,
  TextInput,
  CurrencyInput,
  Switch,
  StatCard,
  SearchInput,
  Select,
  ActionButton,
  Pagination,
} from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import { useDebouncedValue } from '../utils/useDebouncedValue';
import DataState from '../components/DataState';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;

const Section = styled.div`
  margin-bottom: 1.5rem;
`;

const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;

  span {
    font-size: 0.85rem;
    color: ${(p) => p.theme.textSecondary};
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 1.25rem;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: center;

  > *:first-child {
    flex: 1;
    min-width: 240px;
  }
`;

const Muted = styled.span`
  color: ${(p) => p.theme.textMuted};
`;

const STATUS_LABELS: Record<CashbackStatus, string> = {
  AVAILABLE: 'Disponível',
  PENDING: 'Pendente',
  REDEEMED: 'Resgatado',
  EXPIRED: 'Expirado',
};

const STATUS_FILTER = [
  { value: '', label: 'Todos os status' },
  { value: 'AVAILABLE', label: 'Disponível' },
  { value: 'PENDING', label: 'Pendente' },
  { value: 'REDEEMED', label: 'Resgatado' },
  { value: 'EXPIRED', label: 'Expirado' },
];

/** Map a cashback entry status to a Badge tone. */
function statusTone(status: CashbackStatus): Tone {
  if (status === 'AVAILABLE') return 'success';
  if (status === 'PENDING') return 'warning';
  if (status === 'EXPIRED') return 'danger';
  return 'secondary';
}

/** Merchant panel screen to configure cashback and review generated entries. */
const CashbackPage: React.FC = () => {
  const { showNotification } = useNotification();
  const configState = useAsync(() => adminApi.getCashbackConfig(), []);

  const [form, setForm] = useState<CashbackConfigDto | null>(null);
  const [saving, setSaving] = useState<boolean>(false);

  // Histórico - busca, filtro de status e paginação resolvidos no servidor.
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const debouncedSearch = useDebouncedValue(search.trim(), 350);

  const summaryState = useAsync(
    () => adminApi.getCashbackSummary({ query: debouncedSearch || undefined }),
    [debouncedSearch],
  );
  const entriesState = useAsync(
    () =>
      adminApi.listCashbackEntries({
        page,
        size: pageSize,
        query: debouncedSearch || undefined,
        status: statusFilter ? (statusFilter as CashbackStatus) : undefined,
      }),
    [page, pageSize, debouncedSearch, statusFilter],
  );

  useEffect(() => {
    if (configState.data) setForm(configState.data);
  }, [configState.data]);

  const update = <K extends keyof CashbackConfigDto>(key: K, value: CashbackConfigDto[K]): void => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const save = async (): Promise<void> => {
    if (!form || saving) return;
    setSaving(true);
    try {
      const updated = await adminApi.updateCashbackConfig(form);
      setForm(updated);
      showNotification('Configuração de cashback salva.', 'success');
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível salvar a configuração.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSearchChange = (value: string): void => {
    setSearch(value);
    setPage(0);
  };

  const handleStatusChange = (value: string): void => {
    setStatusFilter(value);
    setPage(0);
  };

  const handlePageSizeChange = (size: number): void => {
    setPageSize(size);
    setPage(0);
  };

  const reloadHistory = (): void => {
    entriesState.reload();
    summaryState.reload();
  };

  const entries = useMemo(() => entriesState.data?.content ?? [], [entriesState.data]);
  const summary = summaryState.data;
  const totalPages = entriesState.data?.totalPages ?? 0;
  const totalItems = entriesState.data?.totalElements;

  return (
    <>
      <PageHeader
        title="Cashback"
        subtitle="Defina as regras de cashback da sua loja e acompanhe o histórico."
      />

      <Section>
        <Card>
          <CardHeader>
            <h3>Configuração de cashback</h3>
          </CardHeader>
          <CardBody>
            <DataState
              loading={configState.loading}
              error={configState.error}
              onRetry={configState.reload}
            >
              {form && (
                <>
                  <SwitchRow>
                    <span>Cashback ativo para novas compras</span>
                    <Switch checked={form.enabled} onChange={(v) => update('enabled', v)} />
                  </SwitchRow>

                  <FormGrid>
                    <TextInput
                      label="Percentual (%)"
                      type="number"
                      min={0}
                      value={form.percent}
                      disabled={!form.enabled}
                      onChange={(e) => update('percent', Number(e.target.value))}
                    />
                    <CurrencyInput
                      label="Compra mínima"
                      value={form.minPurchase}
                      disabled={!form.enabled}
                      onChange={(value) => update('minPurchase', value)}
                    />
                    <TextInput
                      label="Validade (dias)"
                      type="number"
                      min={0}
                      value={form.expirationDays}
                      disabled={!form.enabled}
                      onChange={(e) => update('expirationDays', Number(e.target.value))}
                    />
                  </FormGrid>

                  <Actions>
                    <Button onClick={save} isLoading={saving}>
                      Salvar
                    </Button>
                  </Actions>
                </>
              )}
            </DataState>
          </CardBody>
        </Card>
      </Section>

      <StatsGrid>
        <StatCard
          label="Total concedido"
          value={formatCurrency(summary?.totalAmount ?? 0)}
          icon={<Wallet size={18} />}
          iconColor="#07507A"
        />
        <StatCard
          label="Disponível"
          value={formatCurrency(summary?.availableAmount ?? 0)}
          icon={<CircleDollarSign size={18} />}
          iconColor="#1E9E6A"
        />
        <StatCard
          label="Resgatado"
          value={formatCurrency(summary?.redeemedAmount ?? 0)}
          icon={<Gift size={18} />}
          iconColor="#07989A"
        />
        <StatCard
          label="Expirado"
          value={formatCurrency(summary?.expiredAmount ?? 0)}
          icon={<Clock size={18} />}
          iconColor="#DC2626"
        />
      </StatsGrid>

      <Card>
        <CardHeader>
          <h3>Histórico de cashback</h3>
        </CardHeader>
        <CardBody>
          <Toolbar>
            <SearchInput
              placeholder="Buscar por cliente..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
            <Select
              options={STATUS_FILTER}
              value={statusFilter}
              onChange={(e) => handleStatusChange(e.target.value)}
            />
            <ActionButton title="Atualizar" onClick={reloadHistory}>
              <RefreshCw size={16} />
            </ActionButton>
          </Toolbar>

          <DataState
            loading={entriesState.loading}
            error={entriesState.error}
            onRetry={reloadHistory}
            isEmpty={entries.length === 0}
            emptyTitle="Nenhum cashback gerado"
            emptyDescription="Os cashbacks concedidos aos seus clientes aparecerão aqui."
          >
            <TableContainer>
              <Table>
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>Valor</th>
                    <th>Status</th>
                    <th>Gerado em</th>
                    <th>Expira em</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr key={entry.id}>
                      <td>{entry.customerName}</td>
                      <td>{formatCurrency(entry.amount)}</td>
                      <td>
                        <Badge tone={statusTone(entry.status)}>{STATUS_LABELS[entry.status]}</Badge>
                      </td>
                      <td>{formatDate(entry.createdAt)}</td>
                      <td>{entry.expiresAt ? formatDate(entry.expiresAt) : <Muted>-</Muted>}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableContainer>
          </DataState>

          {!entriesState.error && (
            <Pagination
              currentPage={page + 1}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={pageSize}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              disabled={entriesState.loading}
              onPageChange={(next) => setPage(next - 1)}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </CardBody>
      </Card>
    </>
  );
};

export default CashbackPage;
